import React, { useState } from "react";
import { Mail } from "lucide-react";
import { Modal } from "./Modal";
import * as api from "@/src/api/sdk.gen";
import type { WorkspaceItem } from "../../types";
import { getErrorMessage } from "../../types";

export interface InviteWorkspaceMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  workspace: WorkspaceItem;
  onSuccess: () => Promise<void>;
}

export const InviteWorkspaceMemberModal: React.FC<InviteWorkspaceMemberModalProps> = ({
  isOpen,
  onClose,
  slug,
  workspace,
  onSuccess,
}) => {
  const [inviteWsMemberEmail, setInviteWsMemberEmail] = useState("");
  const [inviteWsMemberRole, setInviteWsMemberRole] = useState("member");
  const [invitingWsMember, setInvitingWsMember] = useState(false);
  const [inviteWsMemberStatus, setInviteWsMemberStatus] = useState<string | null>(null);

  const resetForm = () => {
    setInviteWsMemberEmail("");
    setInviteWsMemberRole("member");
    setInviteWsMemberStatus(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || !workspace || !inviteWsMemberEmail.includes("@")) {
      setInviteWsMemberStatus("Error: Please enter a valid email address.");
      return;
    }
    try {
      setInvitingWsMember(true);
      setInviteWsMemberStatus("Sending workspace invitation...");
      const { data, error } = await api.inviteWorkspaceMemberApiV1B2bSlugWorkspacesWsSlugInvitesPost({
        path: { slug, ws_slug: workspace.slug },
        body: {
          email: inviteWsMemberEmail.trim().toLowerCase(),
          role: inviteWsMemberRole,
        },
      });
      if (data) {
        setInviteWsMemberStatus("Workspace invitation sent successfully!");
        setInviteWsMemberEmail("");
        setTimeout(() => {
          handleClose();
        }, 1500);
        await onSuccess();
      } else {
        setInviteWsMemberStatus(`Error: ${getErrorMessage(error, "Failed to send invitation")}`);
      }
    } catch (err) {
      setInviteWsMemberStatus("Error: Network issue connecting to server");
    } finally {
      setInvitingWsMember(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Invite to ${workspace.name}`}
      icon={<Mail className="w-5 h-5 text-indigo-600" />}
      maxWidth="max-w-md"
    >
      {inviteWsMemberStatus && (
        <div
          className={`mb-4 p-3 rounded-xl text-xs font-semibold ${
            inviteWsMemberStatus.startsWith("Workspace invitation sent")
              ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
              : inviteWsMemberStatus.startsWith("Error")
              ? "bg-rose-50 border border-rose-200 text-rose-800"
              : "bg-indigo-50 border border-indigo-200 text-indigo-800"
          }`}
        >
          {inviteWsMemberStatus}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">Invitee Email *</label>
          <input
            type="email"
            required
            placeholder="colleague@company.com"
            value={inviteWsMemberEmail}
            onChange={(e) => setInviteWsMemberEmail(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
          />
          <p className="text-[11px] text-slate-400 mt-1">
            Accepting the invitation will automatically join this user into {workspace.name}.
          </p>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">Workspace Role</label>
          <select
            value={inviteWsMemberRole}
            onChange={(e) => setInviteWsMemberRole(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
          >
            <option value="member">Member</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={invitingWsMember || !inviteWsMemberEmail.includes("@")}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm transition disabled:opacity-50"
          >
            {invitingWsMember ? "Sending..." : "Send Workspace Invite"}
          </button>
        </div>
      </form>
    </Modal>
  );
};
